/**
 * Pure model for the Query panel (ADR-038 Phase F).
 *
 * The DOM-free half of the panel: the catalog projection, the editable
 * predicate tree and its wire serialization, the client-side validity
 * gate that decides whether an edit is allowed to fire a request, and the
 * time-window model. No React and no fetch — so the panel's decisions are
 * unit-testable without a render or a server.
 *
 * # Two predicate shapes, deliberately
 *
 * The wire predicate (feature spec §2.3) is a minimal boolean tree —
 * ``{op, children}`` groups over ``{attribute, operator, value}``
 * comparisons — with no identity and no partial states. The *editable*
 * tree here adds a stable node id (React keys, and the ``detail.path`` the
 * BFF returns for an invalid node maps onto it — REQ-UI-117) and holds
 * operands as **raw strings** so a half-typed value is representable.
 * :func:`serializePredicate` coerces to the wire shape;
 * :func:`validatePredicate` is what stands between a half-typed chip and
 * a request (D14).
 *
 * The client-side validation mirrors the BFF's rules but does not replace
 * them — the BFF re-validates everything against the caller's role-filtered
 * catalog, which is where authorization actually happens (ADR-038
 * §Decision 1 / D9). Client validation exists to keep the auto-run from
 * firing garbage, not to be trusted.
 *
 * Cross-references:
 *   - REQ-UI-113..117 (type selection, predicates, grouping, auto-run).
 *   - REQ-UI-126 (a saved query records a relative window by default).
 *   - Feature spec §2.3 (predicate model), §2.4 (query execution), D14.
 */
import type { components } from "@/lib/api-types.gen";

/** The catalog's per-type descriptor, as served by ``GET /api/query/catalog``. */
export type QueryableType = components["schemas"]["QueryableTypeModel"];
/** One typed, role-visible attribute of a queryable type. */
export type QueryAttribute = components["schemas"]["AttributeModel"];
/** The whole catalog response. */
export type QueryCatalog = components["schemas"]["CatalogResponse"];
/** One page of results. */
export type RecordsResponse = components["schemas"]["RecordsResponse"];
/** One result row — a flat attribute map; values are JSON primitives. */
export type QueryRow = Record<string, unknown>;

/**
 * Every comparison the predicate language admits. Mirrors the BFF's
 * ``Operator`` literal; the catalog tells the UI which are legal per
 * attribute, so this union is for typing, never for offering.
 */
export type QueryOperator =
  | "eq"
  | "ne"
  | "lt"
  | "lte"
  | "gt"
  | "gte"
  | "between"
  | "contains"
  | "not_contains"
  | "starts_with"
  | "in"
  | "not_in"
  | "is_null"
  | "is_not_null"
  | "within_bbox";

/** Operator-facing labels for the chip and the operator picker. */
export const OPERATOR_LABELS: Readonly<Record<QueryOperator, string>> = {
  eq: "is",
  ne: "is not",
  lt: "<",
  lte: "≤",
  gt: ">",
  gte: "≥",
  between: "between",
  contains: "contains",
  not_contains: "does not contain",
  starts_with: "starts with",
  in: "is one of",
  not_in: "is none of",
  is_null: "is empty",
  is_not_null: "is not empty",
  within_bbox: "within box",
};

/** Boolean group operators. */
export type GroupOp = "and" | "or";

/** One editable comparison — a chip. */
export interface ComparisonNode {
  readonly id: string;
  readonly kind: "comparison";
  /** Attribute name from the catalog; empty until the operator picks one. */
  readonly attribute: string;
  /** Operator legal for the attribute's kind; empty until chosen. */
  readonly operator: QueryOperator | "";
  /**
   * Raw operand text, one entry per operand slot. Raw rather than typed so
   * a partially-typed value is representable — coerced by
   * :func:`serializePredicate` and checked by :func:`validatePredicate`.
   */
  readonly operands: readonly string[];
}

/** One editable boolean group — a chip group with an AND/OR toggle. */
export interface GroupNode {
  readonly id: string;
  readonly kind: "group";
  readonly op: GroupOp;
  readonly children: readonly PredicateNode[];
}

/** Either node of the editable predicate tree. */
export type PredicateNode = ComparisonNode | GroupNode;

// Node ids are UI identity only — never persisted, never sent. A module
// counter keeps them unique within a session without pulling in a uuid
// dependency or depending on ``crypto`` being present in every test env.
let nodeSequence = 0;

/** Mint a fresh node id. */
function nextNodeId(): string {
  nodeSequence += 1;
  return `qn${nodeSequence}`;
}

/** A new empty comparison chip. */
export function newComparison(attribute = "", operator: QueryOperator | "" = ""): ComparisonNode {
  return {
    id: nextNodeId(),
    kind: "comparison",
    attribute,
    operator,
    operands: operator === "" ? [] : Array.from({ length: operandCount(operator) }, () => ""),
  };
}

/** A new group, seeded with one empty comparison so it is never empty. */
export function newGroup(op: GroupOp = "and", children?: readonly PredicateNode[]): GroupNode {
  return {
    id: nextNodeId(),
    kind: "group",
    op,
    children: children ?? [newComparison()],
  };
}

/** The empty root predicate — an AND group with no children. */
export function emptyRoot(): GroupNode {
  return { id: nextNodeId(), kind: "group", op: "and", children: [] };
}

/**
 * How many operand slots an operator takes.
 *
 * ``in`` / ``not_in`` take a single slot holding a comma-separated list
 * rather than a variable-length editor: the list is bounded at 100 members
 * server-side, and one field the operator can paste into beats an
 * add-a-row widget for a value set that usually arrives from elsewhere.
 */
export function operandCount(operator: QueryOperator): number {
  switch (operator) {
    case "is_null":
    case "is_not_null":
      return 0;
    case "between":
      return 2;
    case "within_bbox":
      return 4;
    default:
      return 1;
  }
}

// ---- tree operations ----------------------------------------------

/** Replace a node anywhere in the tree, returning a new tree. */
export function updateNode(
  root: GroupNode,
  id: string,
  update: (node: PredicateNode) => PredicateNode,
): GroupNode {
  const visit = (node: PredicateNode): PredicateNode => {
    if (node.id === id) return update(node);
    if (node.kind === "group") {
      return { ...node, children: node.children.map(visit) };
    }
    return node;
  };
  return visit(root) as GroupNode;
}

/**
 * Remove a node anywhere in the tree.
 *
 * A group left empty by the removal is removed too — an empty group has
 * no meaning and the BFF rejects one, so leaving a hollow group on screen
 * would present an unrunnable query as if it were runnable. The root is
 * exempt: an empty root is the legitimate "no predicate" state.
 */
export function removeNode(root: GroupNode, id: string): GroupNode {
  const prune = (group: GroupNode): GroupNode => {
    const children: PredicateNode[] = [];
    for (const child of group.children) {
      if (child.id === id) continue;
      if (child.kind === "group") {
        const pruned = prune(child);
        if (pruned.children.length === 0) continue;
        children.push(pruned);
      } else {
        children.push(child);
      }
    }
    return { ...group, children };
  };
  return prune(root);
}

/** Append a child to the group with the given id. */
export function appendChild(root: GroupNode, groupId: string, child: PredicateNode): GroupNode {
  return updateNode(root, groupId, (node) =>
    node.kind === "group" ? { ...node, children: [...node.children, child] } : node,
  );
}

/**
 * Re-point a comparison at a different attribute.
 *
 * The operator is kept only when it stays legal for the new attribute's
 * kind, and the operands are re-sized to the (possibly different) slot
 * count — carrying a stale "between" pair onto an enum would produce a
 * chip that looks edited but cannot run.
 */
export function retargetComparison(
  node: ComparisonNode,
  attribute: QueryAttribute,
): ComparisonNode {
  // The catalog only ever emits operators from the BFF's own ``Operator``
  // literal, but openapi-typescript widens the field to ``string[]``.
  const legal = attribute.operators as QueryOperator[];
  // An unfilterable attribute (a bytes column) declares no operators, so
  // the fallback must be able to be empty — hence the explicit union
  // rather than ``legal[0] ?? ""``, which TypeScript narrows away.
  const fallback: QueryOperator | "" = legal.length > 0 ? legal[0] : "";
  const operator: QueryOperator | "" =
    node.operator !== "" && legal.includes(node.operator) ? node.operator : fallback;
  return {
    ...node,
    attribute: attribute.name,
    operator,
    operands:
      operator === ""
        ? []
        : Array.from({ length: operandCount(operator) }, (_, i) => node.operands[i] ?? ""),
  };
}

/** Change a comparison's operator, resizing its operand slots. */
export function setOperator(node: ComparisonNode, operator: QueryOperator): ComparisonNode {
  return {
    ...node,
    operator,
    operands: Array.from({ length: operandCount(operator) }, (_, i) => node.operands[i] ?? ""),
  };
}

/** Set one operand slot of a comparison. */
export function setOperand(node: ComparisonNode, index: number, value: string): ComparisonNode {
  const operands = [...node.operands];
  operands[index] = value;
  return { ...node, operands };
}

/** Total comparisons anywhere in the tree. */
export function countComparisons(node: PredicateNode): number {
  return node.kind === "comparison"
    ? 1
    : node.children.reduce((sum, child) => sum + countComparisons(child), 0);
}

/**
 * Deepest group nesting the *serialized* tree will carry; a lone
 * comparison is depth 0.
 *
 * A group with fewer than two children contributes no level, because
 * :func:`serializePredicate` collapses it — a single-child group is
 * semantically its child. Counting it would reject a query the BFF would
 * happily accept.
 */
export function maxDepth(node: PredicateNode, depth = 0): number {
  if (node.kind === "comparison") return depth;
  const childDepth = node.children.length > 1 ? depth + 1 : depth;
  return node.children.reduce(
    (deepest, child) => Math.max(deepest, maxDepth(child, childDepth)),
    depth,
  );
}

// ---- serialization -------------------------------------------------

/** One comparison on the wire. */
interface WireComparison {
  attribute: string;
  operator: string;
  value: unknown;
}

/** One group on the wire. */
interface WireGroup {
  op: GroupOp;
  children: WirePredicate[];
}

/** Either node on the wire. */
export type WirePredicate = WireComparison | WireGroup;

/** Coerce one raw operand to the JSON type the attribute's kind expects. */
function coerceOperand(raw: string, kind: string): unknown {
  const text = raw.trim();
  switch (kind) {
    case "number":
      return Number(text);
    case "enum":
      return Number.parseInt(text, 10);
    case "boolean":
      return text === "true";
    case "timestamp":
      // The BFF rejects a naive timestamp rather than assuming UTC, so an
      // offset is always attached here (§_coerce_timestamp).
      return new Date(text).toISOString();
    default:
      return text;
  }
}

/** Split a comma-separated ``in`` / ``not_in`` operand into its members. */
export function splitListOperand(raw: string): string[] {
  return raw
    .split(",")
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
}

/**
 * Serialize the editable tree to the wire predicate, or ``null`` when
 * there is nothing to send.
 *
 * An empty root serializes to ``null`` (everything in window), not to an
 * empty group — the BFF rejects an empty group, and "no predicate" is a
 * legitimate query. Callers must have validated first; serialization
 * assumes well-formed operands.
 */
export function serializePredicate(
  root: GroupNode,
  attributes: ReadonlyMap<string, QueryAttribute>,
): WirePredicate | null {
  const visit = (node: PredicateNode): WirePredicate | null => {
    if (node.kind === "group") {
      const children = node.children
        .map(visit)
        .filter((child): child is WirePredicate => child !== null);
      if (children.length === 0) return null;
      if (children.length === 1) return children[0];
      return { op: node.op, children };
    }
    if (node.operator === "" || node.attribute === "") return null;
    const attribute = attributes.get(node.attribute);
    if (attribute === undefined) return null;
    return {
      attribute: node.attribute,
      operator: node.operator,
      value: serializeOperands(node, attribute),
    };
  };
  return visit(root);
}

/** The ``value`` field for one comparison, shaped by its operator. */
function serializeOperands(node: ComparisonNode, attribute: QueryAttribute): unknown {
  const raw = node.operands;
  switch (node.operator) {
    case "is_null":
    case "is_not_null":
      return null;
    case "between":
      return [
        coerceOperand(raw[0] ?? "", attribute.kind),
        coerceOperand(raw[1] ?? "", attribute.kind),
      ];
    case "within_bbox":
      return raw.slice(0, 4).map((value) => Number(value.trim()));
    case "in":
    case "not_in":
      return splitListOperand(raw[0] ?? "").map((member) => coerceOperand(member, attribute.kind));
    default:
      return coerceOperand(raw[0] ?? "", attribute.kind);
  }
}

/**
 * Rebuild the editable tree from a stored wire predicate — the re-open
 * half of a saved query (REQ-UI-125).
 *
 * Lossy in one direction only, and deliberately: operands come back as
 * the strings the editors bind to, so a stored number renders as its
 * text. Round-tripping through :func:`serializePredicate` re-derives the
 * same typed values.
 *
 * **Unknown nodes are kept, not dropped.** A comparison naming an
 * attribute the catalog no longer offers becomes a chip that
 * :func:`validatePredicate` marks with "Unknown attribute" — the operator
 * sees what the saved query actually said and can fix it. Silently
 * discarding it would change the meaning of their query without telling
 * them, which is the worse failure.
 */
export function deserializePredicate(
  wire: unknown,
  attributes: ReadonlyMap<string, QueryAttribute>,
): GroupNode {
  const root = emptyRoot();
  const node = parseWireNode(wire, attributes);
  return node === null ? root : { ...root, children: [node] };
}

/** Parse one wire node into an editable node, or ``null`` when malformed. */
function parseWireNode(
  wire: unknown,
  attributes: ReadonlyMap<string, QueryAttribute>,
): PredicateNode | null {
  if (typeof wire !== "object" || wire === null) return null;
  const record = wire as Record<string, unknown>;

  if (record.op === "and" || record.op === "or") {
    const children = Array.isArray(record.children)
      ? record.children
          .map((child) => parseWireNode(child, attributes))
          .filter((child): child is PredicateNode => child !== null)
      : [];
    if (children.length === 0) return null;
    return { id: nextNodeId(), kind: "group", op: record.op, children };
  }

  if (typeof record.attribute !== "string" || typeof record.operator !== "string") {
    return null;
  }
  const operator = record.operator as QueryOperator;
  const attribute = attributes.get(record.attribute);
  return {
    id: nextNodeId(),
    kind: "comparison",
    attribute: record.attribute,
    operator,
    operands: operandsFromWire(record.value, operator, attribute),
  };
}

/** Render a stored ``value`` back into the editors' raw operand slots. */
function operandsFromWire(
  value: unknown,
  operator: QueryOperator,
  attribute: QueryAttribute | undefined,
): readonly string[] {
  const slots = operandCount(operator);
  if (slots === 0) return [];
  if (operator === "in" || operator === "not_in") {
    // One pasteable field, so the stored list is re-joined.
    return [Array.isArray(value) ? value.map((member) => String(member)).join(", ") : ""];
  }
  if (Array.isArray(value)) {
    return Array.from({ length: slots }, (_, i) =>
      value[i] === undefined ? "" : renderOperand(value[i], attribute),
    );
  }
  return Array.from({ length: slots }, (_, i) => (i === 0 ? renderOperand(value, attribute) : ""));
}

/** One stored operand as the text its editor binds to. */
function renderOperand(value: unknown, attribute: QueryAttribute | undefined): string {
  if (value === null || value === undefined) return "";
  if (attribute?.kind === "timestamp" && typeof value === "string") {
    // ``datetime-local`` binds to "YYYY-MM-DDTHH:mm" without an offset.
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? value : parsed.toISOString().slice(0, 16);
  }
  return String(value);
}

// ---- validation (the D14 auto-run gate) ----------------------------

/** The outcome of a client-side predicate check. */
export interface PredicateValidation {
  /** Whether the query may be issued. */
  readonly valid: boolean;
  /** Per-node message, keyed by node id — rendered against the chip. */
  readonly errors: Readonly<Record<string, string>>;
  /** A whole-query problem (over a bound) with no single offending node. */
  readonly formError: string | null;
}

/** Whether an operand slot's raw text parses for the attribute's kind. */
function operandError(raw: string, attribute: QueryAttribute): string | null {
  const text = raw.trim();
  if (text === "") return "Enter a value";
  switch (attribute.kind) {
    case "number":
      return Number.isFinite(Number(text)) ? null : "Enter a number";
    case "enum": {
      const parsed = Number.parseInt(text, 10);
      return attribute.enum_values?.some((v) => v.value === parsed) === true
        ? null
        : "Choose a value";
    }
    case "boolean":
      return text === "true" || text === "false" ? null : "Choose true or false";
    case "timestamp":
      return Number.isNaN(new Date(text).getTime()) ? "Enter a valid time" : null;
    case "string":
    case "id":
      return text.length > 256 ? "Value is longer than 256 characters" : null;
    default:
      return null;
  }
}

/**
 * Check the editable tree against the catalog and the BFF's bounds.
 *
 * This is the gate REQ-UI-117 / D14 describe: an invalid chip shows its
 * error inline and **no request is issued**. Bounds come from the catalog
 * response rather than being hard-coded, so a server-side change to the
 * limits reaches the client without a UI release.
 */
export function validatePredicate(
  root: GroupNode,
  attributes: ReadonlyMap<string, QueryAttribute>,
  bounds: { maxComparisons: number; maxNestingDepth: number },
): PredicateValidation {
  const errors: Record<string, string> = {};

  const visit = (node: PredicateNode, isRoot: boolean): void => {
    if (node.kind === "group") {
      // An empty *nested* group is unrunnable (the BFF rejects one), but an
      // empty ROOT is the legitimate "no predicate — everything in window"
      // state and must not be flagged.
      if (node.children.length === 0 && !isRoot) {
        errors[node.id] = "Add a condition or remove this group";
      }
      node.children.forEach((child) => visit(child, false));
      return;
    }
    if (node.attribute === "") {
      errors[node.id] = "Choose an attribute";
      return;
    }
    const attribute = attributes.get(node.attribute);
    if (attribute === undefined) {
      // The attribute vanished from the catalog — a re-opened saved query
      // against a type whose visible attributes changed, or a role change.
      errors[node.id] = `Unknown attribute "${node.attribute}"`;
      return;
    }
    if (node.operator === "") {
      errors[node.id] = "Choose an operator";
      return;
    }
    if (!attribute.operators.includes(node.operator)) {
      errors[node.id] =
        `"${OPERATOR_LABELS[node.operator]}" is not available for ${attribute.label}`;
      return;
    }
    if (node.operator === "in" || node.operator === "not_in") {
      const members = splitListOperand(node.operands[0] ?? "");
      if (members.length === 0) {
        errors[node.id] = "Enter one or more comma-separated values";
        return;
      }
      if (members.length > 100) {
        errors[node.id] = "Lists are limited to 100 values";
        return;
      }
      for (const member of members) {
        const problem = operandError(member, attribute);
        if (problem !== null) {
          errors[node.id] = problem;
          return;
        }
      }
      return;
    }
    for (let i = 0; i < operandCount(node.operator); i += 1) {
      const problem = operandError(node.operands[i] ?? "", attribute);
      if (problem !== null) {
        errors[node.id] = problem;
        return;
      }
    }
    if (node.operator === "between") {
      const [low, high] = node.operands;
      if (compareOperands(low ?? "", high ?? "", attribute.kind) > 0) {
        errors[node.id] = "Enter the lower value first";
      }
    }
    if (node.operator === "within_bbox") {
      const [minLon, minLat, maxLon, maxLat] = node.operands.map((v) => Number(v.trim()));
      if (Number(minLon) >= Number(maxLon) || Number(minLat) >= Number(maxLat)) {
        errors[node.id] = "Enter min values below max values";
      }
    }
  };
  visit(root, true);

  let formError: string | null = null;
  const comparisons = countComparisons(root);
  if (comparisons > bounds.maxComparisons) {
    formError = `This query has ${comparisons} conditions; the limit is ${bounds.maxComparisons}.`;
  } else if (maxDepth(root) > bounds.maxNestingDepth) {
    formError = `Groups are nested ${maxDepth(root)} deep; the limit is ${bounds.maxNestingDepth}.`;
  }

  return {
    valid: Object.keys(errors).length === 0 && formError === null,
    errors,
    formError,
  };
}

/** Order two raw operands for the ``between`` low ≤ high check. */
function compareOperands(low: string, high: string, kind: string): number {
  if (kind === "number" || kind === "enum") {
    return Number(low) - Number(high);
  }
  if (kind === "timestamp") {
    return new Date(low).getTime() - new Date(high).getTime();
  }
  return low.localeCompare(high);
}

// ---- time window ---------------------------------------------------

/** The mandatory time window (D5), in the shape the operator edits. */
export type TimeRangeSelection =
  | { readonly mode: "relative"; readonly durationSeconds: number }
  | { readonly mode: "absolute"; readonly start: string; readonly end: string };

/** Quick relative windows offered in the time control. */
export const RELATIVE_WINDOWS: readonly { id: string; label: string; seconds: number }[] = [
  { id: "15m", label: "Last 15 minutes", seconds: 900 },
  { id: "1h", label: "Last hour", seconds: 3_600 },
  { id: "6h", label: "Last 6 hours", seconds: 21_600 },
  { id: "24h", label: "Last 24 hours", seconds: 86_400 },
  { id: "7d", label: "Last 7 days", seconds: 604_800 },
  { id: "30d", label: "Last 30 days", seconds: 2_592_000 },
];

/**
 * The landing window (D5): the last 24 hours, relative.
 *
 * Relative rather than absolute so a saved query re-runs against a live
 * window rather than the instant it was saved (D18 / REQ-UI-126).
 */
export const DEFAULT_TIME_RANGE: TimeRangeSelection = {
  mode: "relative",
  durationSeconds: 86_400,
};

/**
 * Render the time window for the wire.
 *
 * An unparseable instant is passed through verbatim rather than throwing.
 * This runs on the render path — a half-typed absolute window is a normal
 * intermediate state, and throwing there would unmount the whole panel.
 * The caller gates on :func:`timeRangeError` before issuing a request; if
 * one ever slips through, the BFF rejects it with a message the operator
 * sees, which is a far better failure than a blank panel.
 */
export function toWireTimeRange(
  selection: TimeRangeSelection,
): components["schemas"]["TimeRangeModel"] {
  if (selection.mode === "relative") {
    return { mode: "relative", duration_seconds: selection.durationSeconds };
  }
  return {
    mode: "absolute",
    start: toIsoOrRaw(selection.start),
    end: toIsoOrRaw(selection.end),
  };
}

/** ISO-8601 with an offset, or the raw text when it does not parse. */
function toIsoOrRaw(value: string): string {
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toISOString();
}

/** Whether an absolute window is complete and correctly ordered. */
export function timeRangeError(selection: TimeRangeSelection): string | null {
  if (selection.mode === "relative") {
    return selection.durationSeconds > 0 ? null : "Choose a time window";
  }
  const start = new Date(selection.start).getTime();
  const end = new Date(selection.end).getTime();
  if (Number.isNaN(start) || Number.isNaN(end)) return "Enter a start and end time";
  if (start >= end) return "The start must be before the end";
  return null;
}

// ---- catalog helpers -----------------------------------------------

/** Index a type's attributes by name for O(1) resolution. */
export function attributeMap(type: QueryableType): ReadonlyMap<string, QueryAttribute> {
  return new Map(type.attributes.map((attribute) => [attribute.name, attribute]));
}

/** The attributes a predicate may reference — filterable ones only. */
export function filterableAttributes(type: QueryableType): readonly QueryAttribute[] {
  return type.attributes.filter((attribute) => attribute.filterable);
}

/**
 * The default sort for a type: its time attribute, newest first.
 *
 * Every queryable type declares the attribute its mandatory window binds
 * to, so this needs no per-type table (feature spec §2.2 — Time is the
 * default sort of the common column spine).
 */
export function defaultSortFor(type: QueryableType): readonly { key: string; direction: "desc" }[] {
  return [{ key: type.time_attribute, direction: "desc" }];
}

/**
 * Whether an attribute is a dynamic system-status domain metric (L6).
 *
 * These are data-derived rather than declared — the BFF enumerates the
 * ``domain`` JSONB keys actually present per component kind — and are
 * marked ``indexed: false`` because JSONB extraction is the least
 * index-efficient path (feature spec §2.3.1).
 */
export const DOMAIN_ATTRIBUTE_PREFIX = "domain.";

/** Whether an attribute name is a dynamic domain metric. */
export function isDomainAttribute(name: string): boolean {
  return name.startsWith(DOMAIN_ATTRIBUTE_PREFIX);
}
